import { oc } from "ts-optchain";

import Vue from "vue";
import { mapGetters } from "vuex";

// Icons
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import {
  formatDateTime,
  paginationMixin,
  stringHelper
} from "@/components/mixins";

// components
import {
  CurrencyText,
  PartDetailsLink
} from "@/components";
import ToolTipPopover from "@/components/Elements/ToolTipPopover.vue";
import { PartDetailsTable } from "./PartDetailsTable";
import ViewCrosses from "@/components/Orders/QuickOrders/Dialogs/ViewCrosses/ViewCrosses.vue"
import LoadingSpinner from "@/components/Elements/LoadingSpinner"
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default Vue.extend({
  name: "PartsListGrid",
  components: {
    CurrencyText,
    FontAwesomeIcon,
    PartDetailsLink,
    PartDetailsTable,
    ToolTipPopover,
    ViewCrosses,
    LoadingSpinner,
    DnTable
  },
  mixins: [formatDateTime, paginationMixin, stringHelper],
  props: {
    isBusy: Boolean,
    color: {
      type: String,
      default: "blue",
    },
    parts: Array,
    showLocation: {
      type: Boolean,
      default: false,
    },
    showDealerSku: {
      type: Boolean,
      default: false,
    },
    showOemPricing: {
      type: Boolean,
      default: false,
    },
    showUnitPricing: {
      type: Boolean,
      default: false,
    },
    showTotalPricing: {
      type: Boolean,
      default: false,
    },
    isQuickOrder: {
      type: Boolean,
      default: false
    },
    disableStacking: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    errorIcon: faExclamationTriangle,
    sortBy: null,
    sortDesc: false,
    isAllItemsSelected: true,
    createdFinished: false
  }),
  computed: {

    ...mapGetters(["getContentItem", "hasPermission", "tenantDetails", "IsMcfa", "IsMcfe", "IsMcfs"]),
    isMlap(){
        return this.IsMcfs;
    },
    columns() {
      const cols = [
        "checkbox",
        { key: "index", label: "#", sortable: true },
        "image",
        { key: "PartDescription", label: "Part", sortable: true },
      ];

      if (this.showLocation) {
        cols.push("location");
      }

      if (this.showDealerSku) {
        cols.push("dealerSKU");
      }

      cols.push("stock");
      cols.push({ key: "Quantity", label: this.getContentItem("Qty"), sortable: false });

      if (this.showOemPricing) {
        cols.push({ key: "CrossOEMListPrice", label: "OEM List Price", sortable: true });
      }

      if (this.hasPermission("CanSeeContractPrice") && this.showUnitPricing) {
        cols.push({ key: "UnitPrice", label: this.getContentItem("UnitPrice"), sortable: true });
      }

      if (this.hasPermission("CanSeeContractPrice") && this.showTotalPricing) {
        cols.push({ key: "TotalPrice", label: this.getContentItem("TotalCost"), sortable: true });
      }

      return cols;
    },

    indexedParts() {
      return this.parts.map(
        (part: any, i: number, isUpdating: Boolean, isUpdatingQty: Boolean) =>
          ({ ...part, index: i + 1, isUpdating: false, isUpdatingQty: false })
      );
    },

    updateBtnText() {
      if (this.showDealerSku && !this.showLocation) {
        return "UPDATE SKU";
      } else if (!this.showDealerSku && this.showLocation) {
        return "UPDATE LOC";
      } else {
        return "UPDATE LOC/SKU";
      }
    }

  },
  created() {
    this.allSelectionChanged(true);
    this.showCrossWarningMessage();

    // bootstrap vue checkboxes do not play nice, so I have to set this boolean
    // in order to prevent the selectAll checkbox from being out of sync on load. 
    this.createdFinished = true;
  },
  methods: {
    allSelectionChanged(checked: boolean) {
      const parts = oc(this as IPartsListGridProps).parts([]);

      parts.forEach((row) => this.selectionChanged(row, checked));
    },

    selectionChanged(row: IGridListRowItem, checked: boolean) {
      row.Selected = checked;
      this.$emit("row-selection-updated", row);

      // deselects the all items check box if you play with individual buttons.
      if (this.createdFinished) {
        this.isAllItemsSelected = false;
      }
    },

    updateRow(row: any) {
      let value = Number(row.Quantity);
      
      // PAS-8409 check user input of StockingLocation/DealerSku for quote marks and special chars, then remove them since it breaks the csv
      if (row.newDealerSku) {
        row.newDealerSku = row.newDealerSku.replace(/[^a-zA-Z0-9 ]/g, '');
      }

      if (row.newStockingLocation) {
        row.newStockingLocation = row.newStockingLocation.replace(/[^a-zA-Z0-9 ]/g, '');
      }


      row.StockingLocation = row.newStockingLocation;
      row.DealerSku = row.newDealerSku;


      if (isNaN(value) || value < 1) {
        value = 1;
        this.$store.commit("pushTimedMessage", {
          header: "The quantity must be a number greater than or equal to 1",
          IsError: true,
          seconds: 5,
        });

        if (isNaN(value) || value < row.MinimumOrderQuantity) {
          value = row.MinimumOrderQuantity;     
          this.$store.commit("pushTimedMessage", {
            header: this.format(this.getContentItem("MinimumQuickOrderQuantity"), row.MinimumOrderQuantity),
            IsError: true,
            seconds: 5,
          });
        }
      }
      this.$emit("row-updated", row);
      row.isUpdating = true;
    },

    updateQuantity(target: any, row: any) {
      let value = Number(target.value);

      if (isNaN(value) || value < 1) {
        value = 1;
        this.$store.commit("pushTimedMessage", {
          header: "The quantity must be a number greater than or equal to 1",
          IsError: true,
          seconds: 5,
        });
      }

      if (isNaN(value) || value < row.MinimumOrderQuantity) {
        value = row.MinimumOrderQuantity;
        this.$store.commit("pushTimedMessage", {
          header:  this.format(this.getContentItem("MinimumQuickOrderQuantity"), row.MinimumOrderQuantity),
          IsError: true,
          seconds: 5,
        });
      }

      row.Quantity = value;
      this.$emit("row-updated", row);
      row.isUpdatingQty = true;
    },

    areAllItemsSelected() {
      const parts = oc(this as IPartsListGridProps).parts([]);
      this.isAllItemsSelected = parts.every((x) => x.Selected);

      return this.isAllItemsSelected;
    },

    addCrossPart(params) {
      this.$emit("add-cross-part", params);
    },

    showCrossWarningMessage() {
      let showMessage = false;

      for (let i = 0; i < this.parts.length; i++) {
        if (this.parts[i].PartValidation.ValidationReason === "MultipleXrefMatches") {
          showMessage = true;
        }
      }
      if (showMessage) {
        this.$store.commit("pushTimedMessage", {
          header: "Multiple Crosses Exist",
          message: "You have entered one or more parts with multiple crosses. Please review the options below and make a selection to replace the part(s) entered.",
          IsError: true,
          seconds: 90,
        });
      }
    },

    showValidationWarning(row) {
      let showWarning = !this.showXrefsWarning(row) && row.item.ErrorMessages && row.item.ErrorMessages.length > 0;
      return showWarning
    },

    showXrefsWarning(row) {
      return row.item.StatusMessage.ShortMessage === 'MultipleXrefMatches'
    },

    showPartWarningCard(row) {
      let hasWarnings = this.showValidationWarning(row) === true || this.showXrefsWarning(row) === true

      // The warning card is toggled by this function's return value, but the table row isn't, so do it manually.
      let warningRow = document.getElementById(`parts-table__details_${row.index}_`)
      if (warningRow !== null) {
        if (hasWarnings === true || !row.item.CanShipAir) {
          warningRow.style.display = '';
        } else {
          warningRow.style.display = 'none';
        }
      }

      return hasWarnings;
    },

    showCantShipAir(row){
      return !row.item.CanShipAir && row.item.PartValidation.IsValid;
    },

    supersessionType(supersessionMsg: string) {
      switch (supersessionMsg) {
        case "IsSupersededLong":
          return "single";
        case "IsSupersededLong2":
        case "IsSupersededLong3":
        default:
          return "default";
      }
    },

    addSupersessionPart(partDetails: object) {
      this.$emit("replace-with-supersession", partDetails);
    },

    shouldShowPricing(row) {
      let pricingAllowed = this.hasPermission("CanSeeContractPrice") && !row.item.ErrorMessages.some(error => error === "IsMaoLong" || error.includes("IsSupersededLong") 
                            || error.includes("IsChannelNotAuthorizedLong") || error.includes("ThisItemHasBeenDiscontinuedCall") || error.includes("IsHardFrozenLong") || error.includes("IsSoftFrozenLong"));
      return pricingAllowed
    },

    getPartValidationMessageHtml(messageKey: string, minimumQty: number) {
      if (["IsSoftFrozenLong", "IsMaoLimitExceededLong", "ThisIsAnInvalidPartButThereIsOneOrMoreCrossReferences", "ThisItemHasBeenDiscontinuedCall", "MaoSoftFrozenLong"].includes(messageKey)) {
        return this.format(this.getContentItem(messageKey), this.tenantDetails.SupportPhoneNumber);
      }
      if(messageKey === "MinimumQuantityNotMet"){
        return this.format(this.getContentItem(messageKey)) + minimumQty;
      }
      return this.getContentItem(messageKey);
    },

    isFacingWarehouse(part, warehouseStockQuantity ){
      // facing warehouse is always sorted to first in the list
      let facingWarehouse = part.WarehouseStockQuantities[0];
      return warehouseStockQuantity.WarehouseCode === facingWarehouse.WarehouseCode;
    },
  }
});

interface IPartsListGridProps {
  isBusy: boolean;
  color: string;
  parts: any;
  showLocation: boolean;
  showDealerSku: boolean;
  showOemPricing: boolean;
  showUnitPricing: boolean;
  showTotalPricing: boolean;
}
